import React, { useState, useEffect } from 'react'
import {
  Mail,
  Lock,
  User,
  Loader2,
  CheckCircle2,
  Database,
  AlertCircle,
  ArrowLeft,
  KeyRound,
  Check,
  X,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { XlevenLogo } from '../Common/XlevenLogo'
import { AppFooter } from '../Common/AppFooter'
import SEO from '../Common/SEO'
import { t } from '../../i18n'

interface SignUpViewProps {
  onBack: () => void
  onGoToLogin: () => void
  onSignUpSuccess: () => void
}

export const SignUpView: React.FC<SignUpViewProps> = ({
  onBack,
  onGoToLogin,
  onSignUpSuccess,
}) => {
  const { signup, loginWithGoogle, loginWithKakao, isLoading } = useAuth()
  const { lang } = useLanguage()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [status, setStatus] = useState<'idle' | 'auth' | 'db' | 'success'>(
    'idle',
  )
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')

  // Password validation
  const hasMinLength = password.length >= 8
  const hasLetters = /[A-Za-z]/.test(password)
  const hasNumbers = /\d/.test(password)
  const hasSpecial = /[@$!%*#?&]/.test(password)
  const isPasswordValid = hasMinLength && hasLetters && hasNumbers && hasSpecial

  // 입력값이 변경되면 오류 메시지를 초기화합니다.
  useEffect(() => {
    if (error) setError('')
    if (password && confirmPassword) {
      if (password !== confirmPassword) {
        setPasswordError(t(lang, 'passwordMismatch'))
        setPasswordSuccess('')
      } else {
        setPasswordError('')
        setPasswordSuccess(t(lang, 'passwordMatch'))
      }
    } else {
      setPasswordError('')
      setPasswordSuccess('')
    }
  }, [name, email, password, confirmPassword, lang])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!name || !email || !password || !confirmPassword) {
      setError(t(lang, 'fillAllFields'))
      return
    }

    if (name.length < 2 || name.length > 20) {
      setError(t(lang, 'nicknameError'))
      return
    }

    if (!isPasswordValid) {
      setError(t(lang, 'passwordInvalid'))
      return
    }

    if (password !== confirmPassword) {
      setError(t(lang, 'passwordMismatch'))
      return
    }

    try {
      setStatus('auth')
      await signup(name, email, password)

      setStatus('db')
      setTimeout(() => {
        setStatus('success')
        setTimeout(() => onSignUpSuccess(), 2000)
      }, 800)
    } catch (err: any) {
      console.warn('Signup attempt failed:', err.message)
      setStatus('idle')
      setError(err.message || 'Scouting authorization failed.')
    }
  }

  if (status === 'success') {
    return (
      <div className='min-h-screen flex items-center justify-center p-8 bg-white overflow-hidden relative'>
        <div className='text-center relative z-10 animate-in zoom-in-95 fade-in duration-700'>
          <div className='relative w-24 h-24 mx-auto mb-8'>
            <div className='absolute inset-0 bg-emerald-500/10 rounded-full animate-ping'></div>
            <div className='relative w-full h-full bg-emerald-500 text-white rounded-[40px] flex items-center justify-center shadow-2xl shadow-emerald-100'>
              <CheckCircle2
                size={48}
                strokeWidth={2.5}
              />
            </div>
          </div>
          <h2 className='text-3xl font-black text-slate-900 mb-3 tracking-tighter uppercase'>
            {t(lang, 'accessGranted')}
          </h2>
          <p className='text-slate-400 text-sm font-medium mb-10 max-w-xs mx-auto'>
            {t(lang, 'scoutingProfileSync')}
          </p>

          <div className='flex flex-col items-center gap-4'>
            <div className='flex items-center gap-3 text-emerald-600 text-[10px] font-black uppercase tracking-[0.2em] bg-emerald-50 px-6 py-2.5 rounded-3xl border border-emerald-100'>
              <Database size={14} /> {t(lang, 'dataLinkActive')}
            </div>
            <p className='text-slate-300 text-[9px] font-black uppercase tracking-[0.3em] animate-pulse mt-4'>
              {t(lang, 'redirecting')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-white flex flex-col relative'>
      <SEO title={t(lang, 'createAccount')} />
      <div className='flex-1 flex flex-col items-center justify-center p-6 py-12'>
        <div className='w-full max-w-[420px] animate-in fade-in slide-in-from-bottom-8 duration-700'>
          {/* Header */}
          <div className='flex flex-col items-center mb-10'>
            <div className='mb-6 scale-110 md:hidden'>
              <XlevenLogo />
            </div>
            <h1 className='text-3xl font-black text-slate-900 tracking-tighter mb-2 uppercase text-center'>
              {t(lang, 'createAccount')}
            </h1>
            <p className='text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] text-center'>
              Join the Football Intelligence Network
            </p>
          </div>

          <div className='space-y-8'>
            <div className='grid grid-cols-1 gap-3'>
              <button
                onClick={loginWithGoogle}
                disabled={isLoading}
                className='w-full flex items-center justify-center gap-3 bg-white border border-gray-200 py-3.5 rounded-2xl md:hover:border-gray-400 md:hover:bg-gray-50 transition-all active:scale-[0.98] group'
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                >
                  <path
                    d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                    fill='#4285F4'
                  />
                  <path
                    d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                    fill='#34A853'
                  />
                  <path
                    d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z'
                    fill='#FBBC05'
                  />
                  <path
                    d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                    fill='#EA4335'
                  />
                </svg>
                <span className='text-xs font-black text-slate-700 uppercase tracking-widest'>
                  {t(lang, 'continueWithGoogle')}
                </span>
              </button>

              {/* Kakao Login */}
              <button
                onClick={loginWithKakao}
                disabled={isLoading}
                className='w-full flex items-center justify-center gap-3 bg-[#FEE500] py-3.5 rounded-2xl md:hover:brightness-105 transition-all active:scale-[0.98] group disabled:opacity-50'
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='black'
                >
                  <path d='M12 3c-4.97 0-9 3.185-9 7.115 0 2.558 1.707 4.8 4.315 6.091l-1.091 4.011c-.046.171.144.316.29.215l4.785-3.175c.548.064 1.114.098 1.701.098 4.97 0 9-3.185 9-7.115S16.97 3 12 3z' />
                </svg>
                <span className='text-xs font-black text-black uppercase tracking-widest'>
                  {t(lang, 'continueWithKakao')}
                </span>
              </button>
            </div>

            <div className='flex items-center gap-4'>
              <div className='flex-1 h-px bg-gray-100'></div>
              <span className='text-[9px] font-black text-slate-300 uppercase tracking-[0.3em]'>
                {t(lang, 'or')}
              </span>
              <div className='flex-1 h-px bg-gray-100'></div>
            </div>

            <form
              onSubmit={handleSubmit}
              className='space-y-5'
            >
              <div className='space-y-4'>
                <div className='space-y-1.5'>
                  <label className='text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1'>
                    {t(lang, 'fullName')}
                  </label>
                  <div className='relative group'>
                    <User
                      className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-rose-500 transition-colors'
                      size={18}
                    />
                    <input
                      type='text'
                      placeholder='Agent Nickname'
                      className='w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-12 text-slate-900 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 focus:bg-white transition-all placeholder:text-slate-200'
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={status !== 'idle'}
                      maxLength={20}
                    />
                    <span className='absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-black text-slate-300 uppercase tabular-nums'>
                      {name.length}/20
                    </span>
                  </div>
                </div>
                <div className='space-y-1.5'>
                  <label className='text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1'>
                    {t(lang, 'emailAddress')}
                  </label>
                  <div className='relative group'>
                    <Mail
                      className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-rose-500 transition-colors'
                      size={18}
                    />
                    <input
                      type='email'
                      placeholder='name@example.com'
                      className='w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 focus:bg-white transition-all placeholder:text-slate-200'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={status !== 'idle'}
                    />
                  </div>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div className='space-y-1.5'>
                    <label className='text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1'>
                      {t(lang, 'password')}
                    </label>
                    <div className='relative group'>
                      <KeyRound
                        className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-rose-500 transition-colors'
                        size={18}
                      />
                      <input
                        type='password'
                        placeholder='••••••••'
                        className={`w-full bg-slate-50 border ${password && !isPasswordValid ? 'border-rose-200' : 'border-slate-100'} rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 focus:bg-white transition-all placeholder:text-slate-200`}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value)
                          setShowPasswordRequirements(true)
                        }}
                        onFocus={() => setShowPasswordRequirements(true)}
                        disabled={status !== 'idle'}
                      />
                    </div>

                    {showPasswordRequirements && password.length > 0 && (
                      <div className='px-1 py-2 space-y-2 animate-in fade-in slide-in-from-top-1'>
                        <div className='grid grid-cols-2 gap-x-4 gap-y-1.5'>
                          <div
                            className={`flex items-center gap-1.5 ${hasMinLength ? 'text-emerald-500' : 'text-slate-300'}`}
                          >
                            {hasMinLength ? (
                              <Check
                                size={10}
                                strokeWidth={3}
                              />
                            ) : (
                              <X
                                size={10}
                                strokeWidth={3}
                              />
                            )}
                            <span className='text-[9px] font-black uppercase tracking-tight'>
                              {t(lang, 'passwordMinLength')}
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-1.5 ${hasLetters ? 'text-emerald-500' : 'text-slate-300'}`}
                          >
                            {hasLetters ? (
                              <Check
                                size={10}
                                strokeWidth={3}
                              />
                            ) : (
                              <X
                                size={10}
                                strokeWidth={3}
                              />
                            )}
                            <span className='text-[9px] font-black uppercase tracking-tight'>
                              {t(lang, 'passwordLetters')}
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-1.5 ${hasNumbers ? 'text-emerald-500' : 'text-slate-300'}`}
                          >
                            {hasNumbers ? (
                              <Check
                                size={10}
                                strokeWidth={3}
                              />
                            ) : (
                              <X
                                size={10}
                                strokeWidth={3}
                              />
                            )}
                            <span className='text-[9px] font-black uppercase tracking-tight'>
                              {t(lang, 'passwordNumbers')}
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-1.5 ${hasSpecial ? 'text-emerald-500' : 'text-slate-300'}`}
                          >
                            {hasSpecial ? (
                              <Check
                                size={10}
                                strokeWidth={3}
                              />
                            ) : (
                              <X
                                size={10}
                                strokeWidth={3}
                              />
                            )}
                            <span className='text-[9px] font-black uppercase tracking-tight'>
                              {t(lang, 'passwordSpecial')}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='space-y-1.5'>
                    <label className='text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1'>
                      {t(lang, 'verify')}
                    </label>
                    <div className='relative group'>
                      <KeyRound
                        className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-rose-500 transition-colors'
                        size={18}
                      />
                      <input
                        type='password'
                        placeholder='••••••••'
                        className={`w-full bg-slate-50 border ${passwordError ? 'border-rose-200' : passwordSuccess ? 'border-emerald-200' : 'border-slate-100'} rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 focus:bg-white transition-all placeholder:text-slate-200`}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={status !== 'idle'}
                      />
                    </div>
                    {passwordError && (
                      <p className='text-[9px] font-black text-rose-500 uppercase tracking-tight ml-1 animate-in fade-in slide-in-from-top-1'>
                        {passwordError}
                      </p>
                    )}
                    {passwordSuccess && (
                      <p className='text-[9px] font-black text-emerald-500 uppercase tracking-tight ml-1 animate-in fade-in slide-in-from-top-1'>
                        {passwordSuccess}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className='bg-rose-50 border border-rose-100 p-4 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-right-2'>
                  <AlertCircle
                    size={16}
                    className='text-rose-500 shrink-0'
                  />
                  <p className='text-rose-600 text-[11px] font-black leading-tight uppercase'>
                    {error}
                  </p>
                </div>
              )}

              <div className='pt-6'>
                <button
                  type='submit'
                  disabled={isLoading || status !== 'idle'}
                  className='w-full bg-rose-500 text-white py-5 rounded-[24px] font-black text-xs uppercase tracking-[0.2em] md:hover:bg-rose-600 transition-all shadow-xl shadow-rose-100 disabled:opacity-70 flex items-center justify-center gap-3 active:scale-[0.98]'
                >
                  {status === 'auth' ? (
                    <>
                      <Loader2
                        className='animate-spin'
                        size={20}
                      />{' '}
                      {t(lang, 'authenticating')}
                    </>
                  ) : status === 'db' ? (
                    <>
                      <Database
                        className='animate-bounce'
                        size={20}
                      />{' '}
                      {t(lang, 'securingData')}
                    </>
                  ) : (
                    <>{t(lang, 'joinNow')}</>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className='mt-12 text-center border-t border-slate-50 pt-10 pb-10'>
            <p className='text-slate-400 text-[13px] font-medium'>
              {t(lang, 'alreadyMember')}{' '}
              <button
                onClick={onGoToLogin}
                className='text-slate-900 font-black md:hover:underline underline-offset-4 decoration-2 uppercase tracking-tight ml-1'
              >
                {t(lang, 'signIn')}
              </button>
            </p>
          </div>
        </div>
      </div>
      <AppFooter lang={lang} />
    </div>
  )
}
